// =============================================================================
// supabase/functions/_shared/byod/parseByodResult.ts
// -----------------------------------------------------------------------------
// PURE, dependency-free BYOD parsing + normalisation for the byod-lookup Edge
// Function (Stage 2). No IO and NO imports, so — like contactUpsertPlan — it is
// unit-tested from src/ via vitest (src/services/byodParse.test.ts).
//
// Two jobs:
//   1. parseByodResult(rawModelText, groundingUris) — turn Gemini's grounded,
//      free-text-with-embedded-JSON answer into a clean, typed BYOD suggestion
//      for a responder to review. Because grounding and a forced JSON schema
//      cannot be combined in one Vertex call, the model returns JSON in its text
//      and we extract + validate it here.
//   2. normalizeByodFields(input) — sanitise an arbitrary fields object down to
//      exactly the school_byod POLICY columns (db-198/db-199) before it is written
//      as a band row by the EF's "save" action. Whitelists columns, coerces types,
//      and drops anything that isn't a well-formed value. The band bounds
//      (year_from / year_to) are handled separately (clampYearLevel) — the responder
//      confirms/adjusts them, so they are explicit save params, not part of `fields`.
//
// ANTI-FABRICATION: the source URLs come from the model's GROUNDING metadata
// (the pages Google Search actually returned), not from anything the model wrote
// in prose — so we never surface a URL the model invented. A policy_url is kept
// only if it is a well-formed http(s) URL; the responder still verifies before
// any save (human-in-the-loop).
//
// PRIVACY: nothing here handles child/family data — only a school's BYOD policy.
// =============================================================================

export type ByodStatus = "byod" | "school_supplied" | "unknown";
export type ByodConfidence = "high" | "medium" | "low";

// The reviewable suggestion returned by a lookup — ONE band (the one covering the
// child's year) plus its span. year_levels is a human-readable display label;
// year_from / year_to are the numeric band bounds (Prep=0..Year 12=12) that drive
// school_byod.year_from / year_to. (byod_flags is intentionally NOT AI-generated —
// it is a tool-specific flag set carried by the seed import / set by a responder.)
export interface ByodSuggestion {
  status: ByodStatus;
  device: string | null;
  year_levels: string | null; // display label only (not a stored column)
  year_from: number | null; // band lowest year (0..12); null = unknown
  year_to: number | null; // band highest year (0..12); null = unknown
  loan: boolean | null;
  loan_note: string | null;
  hardship: boolean | null;
  policy_url: string | null;
  notes: string | null;
}

export interface ByodLookupResult {
  suggestion: ByodSuggestion;
  confidence: ByodConfidence;
  sources: string[]; // grounded source URLs (deduped, http(s) only)
  parsed: boolean; // false = the model text couldn't be parsed as JSON
}

// The reviewable result of a WHOLE-SCHOOL (multi-band) lookup — one suggestion per
// distinct band the model returned (BYOD Part 7 / decision B2). Confidence is the
// model's overall confidence in the set; sources are shared across the bands.
export interface ByodBandsResult {
  suggestions: ByodSuggestion[];
  confidence: ByodConfidence;
  sources: string[];
  parsed: boolean; // false = no { "bands": [...] } object in the model text
}

// The sanitised, write-ready POLICY column set for the "save" action. byod_flags is
// a small string array or null; the rest map 1:1 to the school_byod POLICY columns
// (minus the band bounds year_from/year_to, which are explicit save params, and the
// server-managed byod_source / byod_checked_at / byod_policy_year, which the EF sets
// itself). NOTE: db-198 retired the flat byod_year_levels column — the band's span is
// now year_from/year_to, so it is NOT in this write set.
export interface ByodWriteFields {
  byod_status: ByodStatus;
  byod_device: string | null;
  byod_loan: boolean | null;
  byod_loan_note: string | null;
  byod_hardship: boolean | null;
  byod_flags: string[] | null;
  byod_policy_url: string | null;
  byod_notes: string | null;
}

const STATUSES: ByodStatus[] = ["byod", "school_supplied", "unknown"];
const CONFIDENCES: ByodConfidence[] = ["high", "medium", "low"];

// ── small coercers ───────────────────────────────────────────────────────────

function cleanStr(v: unknown): string | null {
  if (typeof v !== "string") return null;
  const t = v.trim();
  return t.length ? t : null;
}

// true / false / null only. Accepts real booleans and the strings "true"/"false"
// (any case); everything else (incl. "unknown", "", numbers) → null.
function triBool(v: unknown): boolean | null {
  if (typeof v === "boolean") return v;
  if (typeof v === "string") {
    const t = v.trim().toLowerCase();
    if (t === "true" || t === "yes") return true;
    if (t === "false" || t === "no") return false;
  }
  return null;
}

// A band-bound year level on the Prep = 0 … Year 12 = 12 scale. Accepts an integer
// or an integer-valued string; anything non-integer or out of 0..12 → null (dropped,
// per the plan — a bad bound is better absent than wrong).
export function clampYearLevel(v: unknown): number | null {
  const n = typeof v === "number" ? v : typeof v === "string" && v.trim() !== "" ? Number(v) : Number.NaN;
  return Number.isInteger(n) && n >= 0 && n <= 12 ? n : null;
}

function asStatus(v: unknown): ByodStatus {
  const t = typeof v === "string" ? v.trim().toLowerCase().replace(/[\s-]+/g, "_") : "";
  return (STATUSES as string[]).includes(t) ? (t as ByodStatus) : "unknown";
}

function asConfidence(v: unknown): ByodConfidence {
  const t = typeof v === "string" ? v.trim().toLowerCase() : "";
  return (CONFIDENCES as string[]).includes(t) ? (t as ByodConfidence) : "low";
}

// Keep only a well-formed http(s) URL; everything else → null. This is the guard
// against a fabricated / relative / junk policy link.
export function cleanUrl(v: unknown): string | null {
  const s = cleanStr(v);
  if (!s) return null;
  try {
    const u = new URL(s);
    return u.protocol === "http:" || u.protocol === "https:" ? u.toString() : null;
  } catch {
    return null;
  }
}

function cleanUrlList(list: unknown): string[] {
  if (!Array.isArray(list)) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const item of list) {
    const u = cleanUrl(item);
    if (u && !seen.has(u)) {
      seen.add(u);
      out.push(u);
    }
  }
  return out;
}

function cleanFlags(v: unknown): string[] | null {
  if (!Array.isArray(v)) return null;
  const out: string[] = [];
  const seen = new Set<string>();
  for (const item of v) {
    const s = cleanStr(item);
    if (s && !seen.has(s)) {
      seen.add(s);
      out.push(s);
    }
  }
  return out.length ? out : null;
}

// ── JSON extraction ──────────────────────────────────────────────────────────

// Pull the first JSON object out of a model response that may wrap it in prose or
// a ```json fence. Returns null if nothing parses.
export function extractJsonObject(text: string): Record<string, unknown> | null {
  if (typeof text !== "string" || !text.trim()) return null;

  // 1) a fenced ```json ... ``` (or plain ``` ... ```) block
  const fence = text.match(/```(?:json)?\s*([\s\S]*?)```/i);
  const candidates: string[] = [];
  if (fence && fence[1]) candidates.push(fence[1].trim());

  // 2) the substring from the first "{" to the last "}"
  const first = text.indexOf("{");
  const last = text.lastIndexOf("}");
  if (first !== -1 && last > first) candidates.push(text.slice(first, last + 1));

  // 3) the whole thing, in case it is already bare JSON
  candidates.push(text.trim());

  for (const c of candidates) {
    try {
      const parsed = JSON.parse(c);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return parsed as Record<string, unknown>;
      }
    } catch {
      // try the next candidate
    }
  }
  return null;
}

// ── public API ───────────────────────────────────────────────────────────────

// Normalise a loose object (a model suggestion, or responder-edited fields) into
// the exact school_byod POLICY write columns. Unknown/blank → nulls; status defaults
// to "unknown". Band bounds (year_from/year_to) are NOT here — they are explicit save
// params (clampYearLevel). Safe on any input shape.
export function normalizeByodFields(input: unknown): ByodWriteFields {
  const o = (input && typeof input === "object" ? input : {}) as Record<string, unknown>;
  // Accept both the suggestion keys (status, device, …) and the column keys
  // (byod_status, byod_device, …) so the save action can take either shape.
  const pick = (short: string, col: string) => (col in o ? o[col] : o[short]);
  return {
    byod_status: asStatus(pick("status", "byod_status")),
    byod_device: cleanStr(pick("device", "byod_device")),
    byod_loan: triBool(pick("loan", "byod_loan")),
    byod_loan_note: cleanStr(pick("loan_note", "byod_loan_note")),
    byod_hardship: triBool(pick("hardship", "byod_hardship")),
    byod_flags: cleanFlags(pick("flags", "byod_flags")),
    byod_policy_url: cleanUrl(pick("policy_url", "byod_policy_url")),
    byod_notes: cleanStr(pick("notes", "byod_notes")),
  };
}

// Map ONE loose band/suggestion object into a clean ByodSuggestion. Shared by the
// single-band and multi-band parsers so the field mapping can't drift between them.
function suggestionFromObject(obj: Record<string, unknown>): ByodSuggestion {
  const fields = normalizeByodFields(obj);
  return {
    status: fields.byod_status,
    device: fields.byod_device,
    // year_levels is a display label (not a stored column); the band bounds are numeric.
    year_levels: cleanStr(obj.year_levels),
    year_from: clampYearLevel(obj.year_from),
    year_to: clampYearLevel(obj.year_to),
    loan: fields.byod_loan,
    loan_note: fields.byod_loan_note,
    hardship: fields.byod_hardship,
    policy_url: fields.byod_policy_url,
    notes: fields.byod_notes,
  };
}

const EMPTY_SUGGESTION: ByodSuggestion = {
  status: "unknown",
  device: null,
  year_levels: null,
  year_from: null,
  year_to: null,
  loan: null,
  loan_note: null,
  hardship: null,
  policy_url: null,
  notes: null,
};

// Turn Gemini's grounded answer + the grounding source URLs into a reviewable
// suggestion. `groundingUris` are the pages Google Search actually returned
// (extracted by the EF from candidates[].groundingMetadata) — the trusted source
// list. A policy_url the model asserts is kept only if it is a well-formed URL.
export function parseByodResult(rawModelText: string, groundingUris: unknown = []): ByodLookupResult {
  const sources = cleanUrlList(groundingUris);
  const obj = extractJsonObject(rawModelText);

  if (!obj) {
    return { suggestion: { ...EMPTY_SUGGESTION }, confidence: "low", sources, parsed: false };
  }

  return {
    suggestion: suggestionFromObject(obj),
    confidence: asConfidence(obj.confidence),
    sources,
    parsed: true,
  };
}

// Parse a WHOLE-SCHOOL (multi-band) answer (BYOD Part 7 / decision B2): the model
// returns a single object { "bands": [ …band objects… ], "confidence": … }. Each
// band is mapped + clamped exactly like a single suggestion. `parsed` is false when
// there is no object or no `bands` ARRAY (garbage / a refusal); an EMPTY bands array
// is a valid "no bands found" (parsed:true, suggestions:[]). Bands are returned as
// mapped (bounds clamped) — the save_bands path validates each span before writing.
export function parseByodBandsResult(
  rawModelText: string,
  groundingUris: unknown = []
): ByodBandsResult {
  const sources = cleanUrlList(groundingUris);
  const obj = extractJsonObject(rawModelText);
  const rawBands = obj && Array.isArray(obj.bands) ? (obj.bands as unknown[]) : null;

  if (!obj || !rawBands) {
    return { suggestions: [], confidence: "low", sources, parsed: false };
  }

  const suggestions = rawBands
    .filter((b): b is Record<string, unknown> => !!b && typeof b === "object" && !Array.isArray(b))
    .map((b) => suggestionFromObject(b));

  return {
    suggestions,
    confidence: asConfidence(obj.confidence),
    sources,
    parsed: true,
  };
}

// The policy YEAR a lookup/save applies to, given the annual review month/day
// (the admin-configurable rollover — schools publish next year's policy around
// mid-December). A date ON OR AFTER the review date rolls to next year; before
// it, the current year. `now` is injectable for tests.
export function policyYearFor(now: Date, reviewMonth: number, reviewDay: number): number {
  const y = now.getFullYear();
  const review = new Date(y, reviewMonth - 1, reviewDay);
  return now >= review ? y + 1 : y;
}

// Convenience with the default 15-December rollover.
export function currentPolicyYear(now: Date = new Date()): number {
  return policyYearFor(now, 12, 15);
}
